// src/components/AdProviderFilter.js

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../constants/constants';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  TextField,
  Button,
} from '@mui/material';

const AdProviderFilter = ({ userLevel, privileges, setFilters, hasAccess, showSnackbar }) => {
  // State variables for filters
  const [districts, setDistricts] = useState([]);
  const [taluks, setTaluks] = useState([]);
  const [pincodes, setPincodes] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedTaluk, setSelectedTaluk] = useState('');
  const [selectedPincode, setSelectedPincode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');

  useEffect(() => {
    if (userLevel === 1 || userLevel === 2) {
      // Fetch districts for user levels 1 and 2
      fetchDistricts();
    } else if (userLevel === 3 || (userLevel > 3 && hasAccess())) {
      // For higher user levels, set pincodes based on privileges
      setPincodes(privileges.pincodes || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLevel, privileges]);

  // Function to fetch districts
  const fetchDistricts = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/districts`);
      if (response.status === 200) {
        setDistricts(response.data.districts || response.data);
      } else {
        console.error('Failed to fetch districts');
      }
    } catch (error) {
      console.error('Error fetching districts:', error);
    }
  };

  // Function to fetch taluks based on districtId
  const fetchTaluks = async (districtId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/taluks/district/${districtId}`);
      if (response.status === 200) {
        setTaluks(response.data.taluks || response.data);
      } else {
        setTaluks([]);
        console.error('Failed to fetch taluks');
      }
    } catch (error) {
      setTaluks([]);
      console.error('Error fetching taluks:', error);
    }
  };

  // Function to fetch pincodes based on talukId
  const fetchPincodes = async (talukId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/pincodes/taluk/${talukId}`);
      if (response.status === 200) {
        setPincodes(response.data.pincodes || []);
      } else {
        setPincodes([]);
        console.error('Failed to fetch pincodes');
      }
    } catch (error) {
      setPincodes([]);
      console.error('Error fetching pincodes:', error);
    }
  };

  // Handler for district change
  const handleDistrictChange = (event) => {
    const districtId = event.target.value;
    setSelectedDistrict(districtId);
    setSelectedTaluk('');
    setSelectedPincode('');
    setTaluks([]);
    setPincodes([]);

    if (districtId) {
      // Fetch taluks for the selected district
      fetchTaluks(districtId);
      // Update filters: districtId, reset talukId and pincode
      setFilters({
        districtId,
        talukId: null,
        pincode: null,
        phoneNumber: null,
      });
    } else {
      // If district is cleared, reset all filters
      setFilters({
        districtId: null,
        talukId: null,
        pincode: null,
        phoneNumber: null,
      });
    }
  };

  // Handler for taluk change
  const handleTalukChange = (event) => {
    const talukId = event.target.value;
    setSelectedTaluk(talukId);
    setSelectedPincode('');
    setPincodes([]);

    if (talukId) {
      // Fetch pincodes for the selected taluk
      fetchPincodes(talukId);
      // Update filters: talukId, reset pincode
      setFilters((prevFilters) => ({
        ...prevFilters,
        talukId,
        pincode: null,
        phoneNumber: null,
      }));
    } else {
      // If taluk is cleared, reset talukId and pincode
      setFilters((prevFilters) => ({
        ...prevFilters,
        talukId: null,
        pincode: null,
        phoneNumber: null,
      }));
    }
  };

  // Handler for pincode change
  const handlePincodeChange = (event) => {
    const pincode = event.target.value;
    setSelectedPincode(pincode);

    if (pincode) {
      if (userLevel === 1 || userLevel === 2) {
        // For user levels 1 and 2, pincode is based on selected taluk
        setFilters((prevFilters) => ({
          ...prevFilters,
          pincode,
          phoneNumber: null,
        }));
      } else {
        // For higher user levels, pincode is based on privileges
        setFilters({
          pincode,
          districtId: null,
          talukId: null,
          phoneNumber: null,
        });
      }
    } else {
      // If pincode is cleared, reset pincode
      setFilters((prevFilters) => ({
        ...prevFilters,
        pincode: null,
        phoneNumber: null,
      }));
    }
  };

  // Handler for phone number input change
  const handlePhoneNumberChange = (event) => {
    const phone = event.target.value;
    setPhoneNumber(phone);
  };

  // Handler for phone number search submit
  const handleSubmit = () => {
    // Validate phone number
    const phoneRegex = /^[6-9]\d{9}$/;
    if (phoneNumber && !phoneRegex.test(phoneNumber)) {
      showSnackbar('Please enter a valid 10-digit phone number starting with 6-9.', 'error');
      return;
    }

    if (!phoneNumber) {
      showSnackbar('Please enter a phone number to search.', 'warning');
      return;
    }

    // Update filters with phone number, reset other filters
    setFilters({
      phoneNumber: phoneNumber || null,
      districtId: null,
      talukId: null,
      pincode: null,
    });
  };

  return (
    <Grid container spacing={2} alignItems="center" style={{ marginTop: '20px' }}>
      {/* First Line: District, Taluk, Pincode */}
      {(userLevel === 1 || userLevel === 2) ? (
        <React.Fragment>
          {/* District Filter */}
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel id="district-label">District</InputLabel>
              <Select
                labelId="district-label"
                value={selectedDistrict}
                label="District"
                onChange={handleDistrictChange}
              >
                {districts.map((district) => (
                  <MenuItem key={district._id} value={district._id}>
                    {district.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Constituency Filter */}
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth disabled={taluks.length === 0}>
              <InputLabel id="taluk-label">CONSTITUENCY</InputLabel>
              <Select
                labelId="taluk-label"
                value={selectedTaluk}
                label="CONSTITUENCY"
                onChange={handleTalukChange}
              >
                {taluks.length > 0 ? (
                  taluks.map((taluk) => (
                    <MenuItem key={taluk._id} value={taluk._id}>
                      {taluk.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="">
                    <em>No constituency available</em>
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>

          {/* Pincode Filter */}
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth disabled={pincodes.length === 0}>
              <InputLabel id="pincode-label">Pincode</InputLabel>
              <Select
                labelId="pincode-label"
                value={selectedPincode}
                label="Pincode"
                onChange={handlePincodeChange}
              >
                {pincodes.length > 0 ? (
                  pincodes.map((pincode) => (
                    <MenuItem key={pincode} value={pincode}>
                      {pincode}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="">
                    <em>No pincodes available</em>
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>
        </React.Fragment>
      ) : (
        // For user levels 3 or higher, show pincode filter based on privileges
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth disabled={pincodes.length === 0}>
            <InputLabel id="pincode-label">Pincode</InputLabel>
            <Select
              labelId="pincode-label"
              value={selectedPincode}
              label="Pincode"
              onChange={handlePincodeChange}
            >
              {pincodes.length > 0 ? (
                pincodes.map((pincode) => (
                  <MenuItem key={pincode} value={pincode}>
                    {pincode}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="">
                  <em>No pincodes available</em>
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </Grid>
      )}

      {/* Second Line: Phone Number Search */}
      <Grid item xs={12} sm={8}>
        <TextField
          fullWidth
          label="Search by Phone Number"
          value={phoneNumber}
          onChange={handlePhoneNumberChange}
          type="text"
          inputProps={{ maxLength: 10 }}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Button variant="contained" color="primary" fullWidth onClick={handleSubmit}>
          Search
        </Button>
      </Grid>
    </Grid>
  );
};

export default AdProviderFilter;
